import React from 'react'
import { Stack } from 'expo-router'
import { ScrollView, Text, TouchableOpacity, View } from 'react-native'
import { PlayerDotsStrip } from '../../../components/game/PlayerDotsStrip'
import { PlayerDisc } from '../../../components/game/PlayerDisc'
import { useTheme } from '../../../hooks/useTheme'
import { game } from '../../../preview/fixtures/cardBonusOnly'
import { buildActiveGameViewProps } from '../../../preview/buildActiveGameViewProps'
import type { GameState } from '../../../lib/gameState'
import { TEAM_IDS, type TeamId, type TeamShapeName } from '@warcarp/api-types'

const NAMES = ['Ernest', 'Ophelia', 'Tamerlane', 'Yara', 'Persephone', 'Genghis', 'Boudica', 'Zanzibar', 'Hannibal']
const baseState = buildActiveGameViewProps(game).gameState

function fixture(count: number) {
  const players = NAMES.slice(0, count).map((displayName, index) => ({
    ...game.players[0]!, id: `dots-${index}`, userId: `dots-user-${index}`,
    displayName, slot: index + 1, playerIndex: index, colorSlot: index + 1, isBot: false,
  }))
  const state: GameState = {
    ...baseState, players: players.map((player) => String(player.slot)), turnIndex: 0,
    map: { ...baseState.map, territories: baseState.map.territories.map((territory, index) => ({ ...territory, ownerId: String((index % count) + 1) })) },
  }
  return { players, state }
}

/**
 * A team game: `teams` teams of `size`, turn order interleaved (A1 B1 A2 B2),
 * as `buildTurnOrder` deals it at start. Each dot is drawn in its team's
 * shape.
 */
function teamFixture(size: number, teams: number) {
  const { players, state } = fixture(size * teams)
  const membership: Record<string, TeamId> = {}
  players.forEach((player, index) => { membership[String(player.slot)] = TEAM_IDS[Math.floor(index / size)]! })
  const order: string[] = []
  for (let member = 0; member < size; member++) {
    for (let team = 0; team < teams; team++) order.push(String(team * size + member + 1))
  }
  return { players, state: { ...state, teams: membership, players: order } as GameState }
}

/** Free-for-all (no shape) first, then each team shape. */
const GLOW_COMPARE: { label: string; shape?: TeamShapeName }[] = [
  { label: 'FFA' }, { label: 'circle', shape: 'circle' }, { label: 'square', shape: 'square' },
  { label: 'diamond', shape: 'diamond' }, { label: 'hexagon', shape: 'hexagon' },
]

/**
 * The glow states as PlayerDotsStrip and the drawer's AvatarChip draw them:
 * the drawer's gold on-turn halo, the drawer's green online halo and the
 * strip's green online glow.
 */
function glowStates(theme: 'light' | 'dark') {
  const amber = theme === 'dark' ? '#fbbf24' : '#b45309'
  const green = theme === 'dark' ? '#22c55e' : '#15803d'
  return [
    { label: 'turn (drawer)', diameter: 28, ringWidth: 3, ringGap: 1, ringColor: amber, glowColor: amber, glowBleed: 8, marker: { color: amber, width: 10, height: 6, gap: 4 } },
    { label: 'online (drawer)', diameter: 28, ringWidth: 2, ringGap: 1, ringColor: null, glowColor: green, glowBleed: 8, marker: null },
    { label: 'online (strip)', diameter: 16, ringWidth: 1, ringGap: 0, ringColor: green, glowColor: green, glowBleed: 5, marker: null },
  ]
}

/** One glow state across every shape, enlarged ×`scale` (scale 1: actual size). */
function GlowCompareRow({ state, scale }: { state: ReturnType<typeof glowStates>[number]; scale: number }) {
  const fill = '#2a5ae0'
  const cell = (state.diameter + 2 * (state.ringGap + state.ringWidth) + 2 * state.glowBleed + 16) * scale
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      <Text style={{ width: 96 }} className="font-stencil text-xs text-ink">{`${state.label}${scale > 1 ? ` ×${scale}` : ''}`}</Text>
      {GLOW_COMPARE.map(({ label, shape }) => (
        <View key={label} style={{ width: cell, height: cell, alignItems: 'center', justifyContent: 'center' }}>
          <View style={{ transform: [{ scale }] }}>
            <PlayerDisc
              initial="Y"
              fill={fill}
              diameter={state.diameter}
              fontSize={state.diameter * 0.52}
              ringWidth={state.ringWidth}
              ringGap={state.ringGap}
              ringColor={state.ringColor ?? fill}
              glowColor={state.glowColor}
              glowBleed={state.glowBleed}
              marker={state.marker}
              shape={shape}
            />
          </View>
        </View>
      ))}
    </View>
  )
}

/**
 * The real top-bar player strip at every roster size, with the turn ring and
 * live glows forced on, so ring/glow centring can be inspected per platform.
 * Rows: the on-turn player plus one live player (the reported case), then
 * every player live, the densest glow overlap.
 */
export default function PlayerDotsPreview() {
  const { theme, setTheme } = useTheme()
  return (
    <ScrollView className="flex-1 bg-paper">
      <Stack.Screen options={{ title: 'Player dots' }} />
      <View className="flex-row gap-4 p-3">
        {(['light', 'dark'] as const).map((mode) => (
          <TouchableOpacity key={mode} testID={`player-dots-theme-${mode}`} accessibilityRole="button" onPress={() => setTheme(mode)}>
            <Text className={`font-typed ${theme === mode ? 'text-blood' : 'text-ink'}`}>{mode}</Text>
          </TouchableOpacity>
        ))}
      </View>
      <View testID="player-dots-fixture" collapsable={false} className="bg-paper px-3 pb-6 gap-1">
        {[2, 3, 4, 5, 6, 7, 8, 9].map((count) => {
          const { players, state } = fixture(count)
          const oneLive = new Set([players[Math.min(3, count - 1)]!.userId])
          const allLive = new Set(players.map((player) => player.userId))
          return (
            <View key={count} testID={`player-dots-${count}`} collapsable={false}>
              {[{ label: 'turn + 1 live', presence: oneLive }, { label: 'all live', presence: allLive }].map((row) => (
                <View key={row.label} style={{ height: 52, flexDirection: 'row', alignItems: 'center' }}>
                  <Text style={{ width: 96 }} className="font-stencil text-xs text-ink">{`${count} · ${row.label}`}</Text>
                  <PlayerDotsStrip state={state} players={players} presence={row.presence} onOpen={() => {}} alignment="center" />
                </View>
              ))}
            </View>
          )
        })}
        <Text className="font-stencil text-xs text-ink pt-3">Team games</Text>
        {([[2, 2], [2, 3], [3, 2], [2, 4], [3, 3], [4, 2]] as const).map(([size, teams]) => {
          const { players, state } = teamFixture(size, teams)
          const oneLive = new Set([players[1]!.userId])
          return (
            <View key={`${size}x${teams}`} testID={`player-dots-team-${size}x${teams}`} collapsable={false} style={{ height: 52, flexDirection: 'row', alignItems: 'center' }}>
              <Text style={{ width: 96 }} className="font-stencil text-xs text-ink">{Array.from({ length: teams }, () => size).join('v')}</Text>
              <PlayerDotsStrip state={state} players={players} presence={oneLive} onOpen={() => {}} alignment="center" />
            </View>
          )
        })}
        <View testID="player-dots-glow-compare" collapsable={false} className="bg-paper gap-2 pt-3">
          <Text className="font-stencil text-xs text-ink">{`Glow match: ${GLOW_COMPARE.map((entry) => entry.label).join(', ')}`}</Text>
          {glowStates(theme === 'dark' ? 'dark' : 'light').map((state) => (
            <View key={state.label}>
              <GlowCompareRow state={state} scale={3} />
              <GlowCompareRow state={state} scale={1} />
            </View>
          ))}
        </View>
        <Text className="font-stencil text-xs text-ink pt-3">On turn, every shape (all live)</Text>
        {(['circle', 'square', 'diamond', 'hexagon'] as const).map((shape, team) => {
          // 2v2v2v2 deals A1 B1 C1 D1 first, so turn index N is Team N's first member.
          const { players, state } = teamFixture(2, 4)
          const allLive = new Set(players.map((player) => player.userId))
          return (
            <View key={shape} testID={`player-dots-turn-${shape}`} collapsable={false} style={{ height: 52, flexDirection: 'row', alignItems: 'center' }}>
              <Text style={{ width: 96 }} className="font-stencil text-xs text-ink">{shape}</Text>
              <PlayerDotsStrip state={{ ...state, turnIndex: team }} players={players} presence={allLive} onOpen={() => {}} alignment="center" />
            </View>
          )
        })}
      </View>
    </ScrollView>
  )
}
